import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Compass,
  Diamond,
  Library,
  List,
  PenLine,
  Search,
  Star,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  api,
  type WiedzaItem,
  type WiedzaReadingGuide,
  type WiedzaReadingGuidePhase,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

function fold(text: string) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l");
}

function matchesQuery(item: WiedzaItem, q: string) {
  if (!q.trim()) return true;
  const hay = fold([item.title, item.summary, item.path, item.category, ...(item.tags || [])].join(" "));
  return fold(q)
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => hay.includes(token));
}

function formatLabel(fmt: string) {
  if (fmt === "md") return "markdown";
  if (fmt === "html") return "html";
  if (fmt === "pdf") return "pdf";
  return fmt;
}

function isItemDone(item?: WiedzaItem | null) {
  if (!item) return false;
  return Boolean(item.completed || (item.scroll_pct || 0) >= 95);
}

function sortByOrder(a: WiedzaItem, b: WiedzaItem) {
  const ao = a.order ?? 999;
  const bo = b.order ?? 999;
  if (ao !== bo) return ao - bo;
  return a.title.localeCompare(b.title, "pl");
}

/** Hub + dzieci w order (max 2 poziomy). */
function moduleSequence(hubId: string, items: WiedzaItem[]): WiedzaItem[] {
  const hub = items.find((item) => item.id === hubId);
  const kids = items.filter((item) => item.parentId === hubId).sort(sortByOrder);
  return hub ? [hub, ...kids] : kids;
}

function resolveModuleHubId(item: WiedzaItem | undefined, items: WiedzaItem[]): string | null {
  if (!item) return null;
  if (item.parentId) return item.parentId;
  if (item.moduleHub || items.some((row) => row.parentId === item.id)) return item.id;
  return null;
}

function phaseModuleItems(
  phase: WiedzaReadingGuidePhase,
  items: WiedzaItem[],
): WiedzaItem[] {
  const seen = new Set<string>();
  const out: WiedzaItem[] = [];
  for (const step of phase.steps) {
    for (const item of moduleSequence(step.itemId, items)) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}

function phaseDone(phase: WiedzaReadingGuidePhase, itemsById: Map<string, WiedzaItem>, items: WiedzaItem[]) {
  const rows = phaseModuleItems(phase, items);
  if (!rows.length) return false;
  return rows.every((item) => isItemDone(itemsById.get(item.id) || item));
}

function phaseInProgress(
  phase: WiedzaReadingGuidePhase,
  itemsById: Map<string, WiedzaItem>,
  items: WiedzaItem[],
) {
  const rows = phaseModuleItems(phase, items);
  const anyStarted = rows.some((row) => {
    const item = itemsById.get(row.id) || row;
    return item && ((item.scroll_pct || 0) > 0 || item.last_read_at);
  });
  return anyStarted && !phaseDone(phase, itemsById, items);
}

function findNextGuideStep(
  guide: WiedzaReadingGuide,
  itemsById: Map<string, WiedzaItem>,
  items: WiedzaItem[],
) {
  for (const phase of guide.phases) {
    for (const step of phase.steps) {
      for (const row of moduleSequence(step.itemId, items)) {
        const item = itemsById.get(row.id) || row;
        if (item?.available && !isItemDone(item)) return { phase, step, item };
      }
    }
  }
  return null;
}

function findCurrentPhaseId(
  guide: WiedzaReadingGuide,
  itemsById: Map<string, WiedzaItem>,
  items: WiedzaItem[],
) {
  const next = findNextGuideStep(guide, itemsById, items);
  if (next) return next.phase.id;
  return guide.phases[guide.phases.length - 1]?.id || "";
}

function shortPhaseTitle(title: string) {
  return title.replace(/^\d+\.\s*/, "").replace(/\s*\(.*\)\s*$/, "").trim();
}

function phaseProgress(
  phase: WiedzaReadingGuidePhase,
  itemsById: Map<string, WiedzaItem>,
  items: WiedzaItem[],
) {
  const rows = phaseModuleItems(phase, items);
  if (!rows.length) return { doneCount: 0, total: 0, pct: 0, avgScroll: 0 };
  let doneCount = 0;
  let scrollSum = 0;
  for (const row of rows) {
    const item = itemsById.get(row.id) || row;
    const pct = isItemDone(item) ? 100 : Math.max(0, Math.min(100, item?.scroll_pct || 0));
    if (pct >= 95) doneCount += 1;
    scrollSum += pct;
  }
  const avgScroll = Math.round(scrollSum / rows.length);
  return { doneCount, total: rows.length, pct: avgScroll, avgScroll };
}

function pathProgress(
  guide: WiedzaReadingGuide,
  itemsById: Map<string, WiedzaItem>,
  items: WiedzaItem[],
) {
  const rows = guide.phases.flatMap((p) => phaseModuleItems(p, items));
  if (!rows.length) return { doneCount: 0, total: 0, pct: 0 };
  let doneCount = 0;
  let scrollSum = 0;
  for (const row of rows) {
    const item = itemsById.get(row.id) || row;
    const pct = isItemDone(item) ? 100 : Math.max(0, Math.min(100, item?.scroll_pct || 0));
    if (pct >= 95) doneCount += 1;
    scrollSum += pct;
  }
  return {
    doneCount,
    total: rows.length,
    pct: Math.round(scrollSum / rows.length),
  };
}

type PhaseTheme = {
  Icon: LucideIcon;
  accent: string;
  soft: string;
  bar: string;
  ring: string;
};

const PHASE_THEMES: Record<string, PhaseTheme> = {
  orientacja: {
    Icon: Compass,
    accent: "text-sky-800",
    soft: "bg-sky-100",
    bar: "bg-sky-600",
    ring: "border-sky-300",
  },
  tresci: {
    Icon: PenLine,
    accent: "text-emerald-800",
    soft: "bg-emerald-100",
    bar: "bg-emerald-600",
    ring: "border-emerald-300",
  },
  oferta: {
    Icon: Diamond,
    accent: "text-violet-800",
    soft: "bg-violet-100",
    bar: "bg-violet-600",
    ring: "border-violet-300",
  },
  tydzien: {
    Icon: CalendarDays,
    accent: "text-amber-900",
    soft: "bg-amber-100",
    bar: "bg-amber-500",
    ring: "border-amber-300",
  },
};

function phaseTheme(id: string): PhaseTheme {
  return (
    PHASE_THEMES[id] || {
      Icon: BookOpen,
      accent: "text-brand",
      soft: "bg-brand/10",
      bar: "bg-brand",
      ring: "border-brand/40",
    }
  );
}

function ProgressBar({
  pct,
  className,
  barClassName,
  always = false,
}: {
  pct: number;
  className?: string;
  barClassName?: string;
  always?: boolean;
}) {
  const value = Math.max(0, Math.min(100, pct || 0));
  if (!always && value <= 0) return null;
  return (
    <div
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-muted", className)}
      title={`${Math.round(value)}%`}
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn("h-full rounded-full bg-brand transition-all", barClassName)}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

function StarButton({
  starred,
  onToggle,
  size = "sm",
}: {
  starred: boolean;
  onToggle: () => void;
  size?: "sm" | "md";
}) {
  return (
    <button
      type="button"
      aria-label={starred ? "Usuń gwiazdkę" : "Dodaj gwiazdkę"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        "rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-amber-500",
        starred && "text-amber-500",
        size === "md" && "p-1.5",
      )}
    >
      <Star className={cn(size === "md" ? "h-5 w-5" : "h-4 w-4", starred && "fill-current")} />
    </button>
  );
}

function ItemCard({
  item,
  categoryLabel,
  onOpen,
  onToggleStar,
}: {
  item: WiedzaItem;
  categoryLabel: string;
  onOpen: () => void;
  onToggleStar: () => void;
}) {
  return (
    <div
      className={cn(
        "h-full w-full rounded-lg border p-3 text-left transition hover:border-brand/40 hover:bg-muted/40",
        !item.available && "opacity-70",
      )}
    >
      <div className="flex items-start gap-1">
        <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium leading-snug">{item.title}</p>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.summary}</p>
            </div>
            {item.featured ? <Badge variant="secondary">start</Badge> : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge variant="outline">{categoryLabel}</Badge>
            <Badge variant="outline">{formatLabel(item.format)}</Badge>
            {item.minutes ? <Badge variant="outline">~{item.minutes} min</Badge> : null}
            {!item.available ? <Badge variant="destructive">brak pliku</Badge> : null}
            {item.completed ? <Badge variant="secondary">przeczytane</Badge> : null}
            {(item.tags || []).slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>
          <ProgressBar pct={item.scroll_pct || 0} className="mt-2" />
        </button>
        <StarButton starred={Boolean(item.starred)} onToggle={onToggleStar} />
      </div>
    </div>
  );
}

function PathAccordion({
  guide,
  itemsById,
  items,
  onOpen,
}: {
  guide: WiedzaReadingGuide;
  itemsById: Map<string, WiedzaItem>;
  items: WiedzaItem[];
  onOpen: (id: string) => void;
}) {
  const currentId = findCurrentPhaseId(guide, itemsById, items);
  const [openId, setOpenId] = useState(currentId);
  const next = findNextGuideStep(guide, itemsById, items);

  useEffect(() => {
    setOpenId(currentId);
  }, [currentId]);

  return (
    <div className="space-y-3">
      {guide.phases.map((phase, idx) => {
        const done = phaseDone(phase, itemsById, items);
        const inProgress = phaseInProgress(phase, itemsById, items);
        const isOpen = openId === phase.id;
        const isCurrent = currentId === phase.id;
        const theme = phaseTheme(phase.id);
        const PhaseIcon = theme.Icon;
        const progress = phaseProgress(phase, itemsById, items);
        const moduleRows = phaseModuleItems(phase, items);
        const statusLabel = done
          ? "Ukończone"
          : inProgress || isCurrent
            ? "W trakcie"
            : "Jeszcze nie";

        return (
          <div
            key={phase.id}
            className={cn(
              "overflow-hidden rounded-xl border bg-card",
              isCurrent && cn("shadow-sm", theme.ring),
              done && !isCurrent && "opacity-85",
            )}
          >
            <button
              type="button"
              className="flex w-full items-start gap-3 px-4 py-3.5 text-left"
              onClick={() => setOpenId(isOpen ? "" : phase.id)}
              aria-expanded={isOpen}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                  theme.soft,
                  theme.accent,
                )}
              >
                {done ? <CheckCircle2 className="h-5 w-5" /> : <PhaseIcon className="h-5 w-5" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold leading-snug">
                    {idx + 1}. {shortPhaseTitle(phase.title)}
                  </span>
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-[10px]",
                      done && "bg-emerald-100 text-emerald-900",
                      (inProgress || isCurrent) && !done && cn(theme.soft, theme.accent),
                    )}
                  >
                    {statusLabel}
                  </Badge>
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  {phase.summary}
                </span>
                <div className="mt-2 flex items-center gap-3">
                  <ProgressBar
                    pct={progress.pct}
                    always
                    className="max-w-[12rem] flex-1"
                    barClassName={theme.bar}
                  />
                  <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
                    {progress.doneCount}/{progress.total} · {progress.pct}%
                  </span>
                </div>
              </span>
              <ChevronDown
                className={cn("mt-2 h-4 w-4 shrink-0 text-muted-foreground transition", isOpen && "rotate-180")}
              />
            </button>
            {isOpen ? (
              <div className="border-t bg-muted/20 px-4 py-3">
                {phase.description ? (
                  <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{phase.description}</p>
                ) : null}
                <ul className="space-y-2">
                  {moduleRows.map((row) => {
                    const item = itemsById.get(row.id) || row;
                    const stepDone = isItemDone(item);
                    const pct = stepDone ? 100 : Math.round(item?.scroll_pct || 0);
                    const isNext = next?.item.id === row.id;
                    const isHub = Boolean(item.moduleHub) || !item.parentId;
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          disabled={!item?.available}
                          onClick={() => onOpen(row.id)}
                          className={cn(
                            "flex w-full items-start gap-3 rounded-lg border border-transparent bg-card px-3 py-3 text-left transition hover:border-border hover:bg-muted/40",
                            isNext && cn("ring-1", theme.ring, theme.soft),
                            !item?.available && "cursor-not-allowed opacity-50",
                          )}
                        >
                          <span className={cn("mt-0.5 shrink-0", stepDone ? theme.accent : "text-muted-foreground")}>
                            {stepDone ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : (
                              <Circle className="h-4 w-4" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className={cn("leading-snug", isHub ? "font-semibold" : "font-medium")}>
                                {item?.title || row.id}
                              </span>
                              {item?.minutes ? (
                                <span className="text-xs text-muted-foreground">~{item.minutes} min</span>
                              ) : null}
                              <Badge
                                variant={stepDone ? "secondary" : pct > 0 ? "outline" : "outline"}
                                className="text-[10px] tabular-nums"
                              >
                                {stepDone ? "gotowe" : pct > 0 ? `${pct}%` : "nieczytane"}
                              </Badge>
                            </span>
                            <ProgressBar
                              pct={pct}
                              always
                              className="mt-2"
                              barClassName={theme.bar}
                            />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ModuleToc({
  sequence,
  selectedId,
  onOpen,
}: {
  sequence: WiedzaItem[];
  selectedId: string;
  onOpen: (id: string) => void;
}) {
  return (
    <nav className="flex h-full flex-col" aria-label="Spis modułu">
      <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        W module
      </p>
      <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
        {sequence.map((item, idx) => {
          const done = isItemDone(item);
          const pct = done ? 100 : Math.round(item.scroll_pct || 0);
          const active = item.id === selectedId;
          const isHub = Boolean(item.moduleHub) || idx === 0;
          const status = done ? "gotowe" : pct > 0 ? `${pct}%` : "nieczytane";
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onOpen(item.id)}
                className={cn(
                  "w-full rounded-lg border px-2.5 py-2 text-left transition",
                  active
                    ? "border-brand/40 bg-brand/10"
                    : "border-transparent hover:border-border hover:bg-muted/50",
                )}
              >
                <span className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-muted-foreground">
                    {done ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
                    ) : (
                      <Circle className="h-3.5 w-3.5" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn("block text-sm leading-snug", isHub ? "font-semibold" : "font-medium")}>
                      {item.title}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                      {item.minutes ? <span>~{item.minutes} min</span> : null}
                      <span className="tabular-nums">{status}</span>
                    </span>
                    <ProgressBar pct={pct} always className="mt-1.5" />
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function extractReadableHtml(raw: string): string {
  const article = raw.match(/<article\b[\s\S]*?<\/article>/i);
  if (article) {
    const styleMatch = raw.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    const style = styleMatch ? `<style>${styleMatch[1]}</style>` : "";
    return `${style}${article[0]}`;
  }
  const body = raw.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (body) return body[1];
  return raw;
}

function HtmlReader({
  html,
  itemId,
  initialPct,
  onProgress,
  onNavigateItem,
  className,
}: {
  html: string;
  itemId: string;
  initialPct: number;
  onProgress: (pct: number, anchor?: string) => void;
  onNavigateItem: (id: string) => void;
  className?: string;
}) {
  const readable = useMemo(() => extractReadableHtml(html), [html]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const restoredRef = useRef<string>("");
  const lastSentRef = useRef(0);

  useEffect(() => {
    restoredRef.current = "";
  }, [itemId, readable]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !readable) return;
    if (restoredRef.current === itemId) return;
    restoredRef.current = itemId;
    const target = Math.max(0, Math.min(100, initialPct || 0));
    requestAnimationFrame(() => {
      const max = el.scrollHeight - el.clientHeight;
      if (max > 0 && target > 1) el.scrollTop = (target / 100) * max;
    });
  }, [itemId, readable, initialPct]);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max <= 0 ? 100 : Math.round((el.scrollTop / max) * 1000) / 10;
    const now = Date.now();
    if (now - lastSentRef.current < 1500 && pct < 95) return;
    lastSentRef.current = now;
    onProgress(pct);
  }, [onProgress]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!href) return;
      try {
        const url = new URL(href, window.location.origin);
        const id = url.searchParams.get("id");
        if (id && (url.pathname.includes("/wiedza") || href.startsWith("?id=") || href.startsWith("/wiedza"))) {
          e.preventDefault();
          onNavigateItem(id);
          return;
        }
      } catch {
        /* ignore */
      }
      if (href.startsWith("?id=")) {
        e.preventDefault();
        onNavigateItem(href.slice(4));
      }
    },
    [onNavigateItem],
  );

  return (
    <div
      ref={scrollerRef}
      onScroll={handleScroll}
      onClick={handleClick}
      className={cn("wiedza-html-host h-full overflow-y-auto bg-[#f7f2ee]", className)}
      dangerouslySetInnerHTML={{ __html: readable }}
    />
  );
}

export default function Wiedza() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("id") || "";
  const isLibrary = params.get("view") === "biblioteka";
  const isReading = Boolean(selectedId);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [tag, setTag] = useState("");
  const [starsOnly, setStarsOnly] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const queryClient = useQueryClient();

  const catalogQuery = useQuery({
    queryKey: ["wiedza-catalog"],
    queryFn: () => api.wiedzaCatalog(),
  });

  const detailQuery = useQuery({
    queryKey: ["wiedza-item", selectedId],
    queryFn: () => api.wiedzaItem(selectedId),
    enabled: Boolean(selectedId),
  });

  const catalog = catalogQuery.data;
  const items = catalog?.items || [];
  const categories = catalog?.categories || [];
  const readingGuide = catalog?.readingGuide;

  const itemsById = useMemo(() => {
    const map = new Map<string, WiedzaItem>();
    items.forEach((item) => map.set(item.id, item));
    return map;
  }, [items]);

  const categoryLabel = useMemo(() => {
    const map = new Map(categories.map((c) => [c.id, c.label]));
    return (id: string) => map.get(id) || id;
  }, [categories]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => (item.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (starsOnly && !item.starred) return false;
      if (category && item.category !== category) return false;
      if (tag && !(item.tags || []).includes(tag)) return false;
      if (!matchesQuery(item, q)) return false;
      return true;
    });
  }, [items, category, tag, q, starsOnly]);

  const topLevel = useMemo(
    () => filtered.filter((item) => !item.parentId || !filtered.some((p) => p.id === item.parentId)),
    [filtered],
  );

  const childrenOf = (parentId: string) => filtered.filter((item) => item.parentId === parentId);

  const recent = useMemo(() => {
    return [...items]
      .filter((item) => item.last_read_at)
      .sort((a, b) => String(b.last_read_at).localeCompare(String(a.last_read_at)))
      .slice(0, 5);
  }, [items]);

  const selected = items.find((item) => item.id === selectedId) || detailQuery.data?.item;
  const moduleHubId = resolveModuleHubId(selected, items);
  const moduleSeq = useMemo(() => {
    if (!moduleHubId) return [];
    return moduleSequence(moduleHubId, items);
  }, [moduleHubId, items]);
  const moduleHub = moduleHubId ? items.find((item) => item.id === moduleHubId) : undefined;
  const moduleIndex = moduleSeq.findIndex((item) => item.id === selectedId);
  const prevModuleItem = moduleIndex > 0 ? moduleSeq[moduleIndex - 1] : undefined;
  const nextModuleItem =
    moduleIndex >= 0 && moduleIndex < moduleSeq.length - 1 ? moduleSeq[moduleIndex + 1] : undefined;

  const nextGuide = useMemo(() => {
    if (!readingGuide?.phases?.length) return null;
    return findNextGuideStep(readingGuide, itemsById, items);
  }, [readingGuide, itemsById, items]);

  const continueItem = useMemo(() => {
    const lastId = catalog?.last_item_id;
    if (!lastId) return undefined;
    const item = items.find((row) => row.id === lastId);
    if (!item || isItemDone(item)) return undefined;
    return item;
  }, [catalog?.last_item_id, items]);

  const ctaItem = continueItem || nextGuide?.item;
  const ctaIsResume = Boolean(continueItem);

  const pathStats = useMemo(() => {
    if (!readingGuide?.phases?.length) return null;
    return pathProgress(readingGuide, itemsById, items);
  }, [readingGuide, itemsById, items]);

  const starMutation = useMutation({
    mutationFn: ({ id, starred }: { id: string; starred: boolean }) => api.wiedzaStar(id, starred),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wiedza-catalog"] }),
  });

  const progressMutation = useMutation({
    mutationFn: ({ id, scroll_pct }: { id: string; scroll_pct: number }) =>
      api.wiedzaProgress(id, { scroll_pct }),
    onSuccess: (row, vars) => {
      queryClient.setQueryData(["wiedza-catalog"], (prev: typeof catalogQuery.data) => {
        if (!prev) return prev;
        return {
          ...prev,
          last_item_id: vars.id,
          items: prev.items.map((item) =>
            item.id === vars.id
              ? {
                  ...item,
                  scroll_pct: row.scroll_pct,
                  completed: row.completed,
                  last_read_at: row.last_read_at || item.last_read_at,
                }
              : item,
          ),
        };
      });
    },
  });

  const openItem = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params);
      next.set("id", id);
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const clearSelection = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("id");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const goLibrary = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("id");
    next.set("view", "biblioteka");
    setParams(next, { replace: false });
  }, [params, setParams]);

  const goHome = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("id");
    next.delete("view");
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    if (!selectedId) return;
    void api.wiedzaLast(selectedId).then(() => {
      queryClient.invalidateQueries({ queryKey: ["wiedza-catalog"] });
    });
  }, [selectedId, queryClient]);

  useEffect(() => {
    if (!isReading) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const tagName = (e.target as HTMLElement | null)?.tagName;
      if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") return;
      clearSelection();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isReading, clearSelection]);

  useEffect(() => {
    if (!isReading) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isReading]);

  useEffect(() => {
    setTocOpen(false);
  }, [selectedId]);

  function toggleStar(item: WiedzaItem) {
    starMutation.mutate({ id: item.id, starred: !item.starred });
  }

  const rawUrl = selectedId ? api.wiedzaRawUrl(selectedId) : "";
  const htmlBody = selected?.format === "html" ? detailQuery.data?.body : null;
  const useInlineHtml = Boolean(
    htmlBody &&
      (selected?.path?.includes("_wiedza-html/") || htmlBody.includes("wiedza-article")),
  );

  const backLabel = isLibrary ? "Biblioteka" : "Ścieżka";
  const crumbModule = moduleHub?.title || (isLibrary ? "Biblioteka" : "Ścieżka");

  if (isReading) {
    return (
      <div className="fixed inset-x-0 top-14 bottom-0 z-20 flex flex-col bg-background md:left-64 md:top-0">
        <header className="shrink-0 border-b bg-card/95 px-3 py-2.5 backdrop-blur md:px-6">
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={clearSelection} className="shrink-0">
              <ArrowLeft className="mr-1 h-4 w-4" />
              {backLabel}
            </Button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] text-muted-foreground">
                {backLabel}
                {moduleHub ? ` / ${crumbModule}` : ""}
              </p>
              <p className="truncate text-sm font-semibold leading-snug md:text-base">
                {selected?.title || selectedId}
              </p>
            </div>
            {moduleSeq.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 md:hidden"
                onClick={() => setTocOpen((v) => !v)}
                aria-expanded={tocOpen}
              >
                <List className="mr-1 h-4 w-4" />
                Spis
              </Button>
            ) : null}
            {selected ? (
              <StarButton
                starred={Boolean(selected.starred)}
                onToggle={() => toggleStar(selected)}
                size="md"
              />
            ) : null}
          </div>
          <ProgressBar pct={selected?.scroll_pct || 0} className="mt-2" />
          {moduleSeq.length > 0 && tocOpen ? (
            <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border bg-card p-2 md:hidden">
              <ModuleToc sequence={moduleSeq} selectedId={selectedId} onOpen={openItem} />
            </div>
          ) : null}
        </header>

        <div
          className={cn(
            "min-h-0 flex-1 bg-[#f7f2ee]",
            moduleSeq.length > 0 && "md:grid md:grid-cols-[17rem_minmax(0,1fr)]",
          )}
        >
          {moduleSeq.length > 0 ? (
            <aside className="hidden min-h-0 border-r bg-card/90 p-3 md:flex md:flex-col">
              <ModuleToc sequence={moduleSeq} selectedId={selectedId} onOpen={openItem} />
            </aside>
          ) : null}

          <div className="relative flex min-h-0 min-w-0 flex-col">
            <div className="min-h-0 flex-1">
              {detailQuery.isLoading ? (
                <p className="p-6 text-sm text-muted-foreground">Ładuję treść…</p>
              ) : detailQuery.isError ? (
                <p className="p-6 text-sm text-destructive">
                  Nie udało się wczytać pliku. Może jeszcze nie ma go w paczce na serwerze.
                </p>
              ) : useInlineHtml ? (
                <HtmlReader
                  html={htmlBody || ""}
                  itemId={selectedId}
                  initialPct={selected?.scroll_pct || 0}
                  onProgress={(pct) => progressMutation.mutate({ id: selectedId, scroll_pct: pct })}
                  onNavigateItem={openItem}
                  className="wiedza-read-article h-full"
                />
              ) : selected?.format === "html" || selected?.format === "pdf" ? (
                <iframe
                  title={selected?.title || "Dokument"}
                  src={rawUrl}
                  className="h-full w-full border-0 bg-white"
                  onLoad={() => {
                    if (selectedId) progressMutation.mutate({ id: selectedId, scroll_pct: 5 });
                  }}
                />
              ) : (
                <div
                  className="wiedza-prose h-full overflow-y-auto px-4 py-5 sm:px-6 md:mx-auto md:max-w-3xl"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    const max = el.scrollHeight - el.clientHeight;
                    const pct = max <= 0 ? 100 : Math.round((el.scrollTop / max) * 1000) / 10;
                    progressMutation.mutate({ id: selectedId, scroll_pct: pct });
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {detailQuery.data?.body || "_Brak treści._"}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            {moduleSeq.length > 0 ? (
              <footer className="shrink-0 border-t bg-card/95 px-3 py-2.5 backdrop-blur md:px-4">
                <div className="mx-auto flex max-w-3xl items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!prevModuleItem}
                    onClick={() => prevModuleItem && openItem(prevModuleItem.id)}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Wstecz
                  </Button>
                  <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                    {moduleIndex >= 0 ? `${moduleIndex + 1} / ${moduleSeq.length}` : null}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!nextModuleItem}
                    onClick={() => nextModuleItem && openItem(nextModuleItem.id)}
                  >
                    Dalej
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </footer>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (isLibrary) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Button type="button" variant="ghost" size="sm" className="-ml-2 mb-1" onClick={goHome}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Ścieżka
            </Button>
            <PageHeader
              title="Biblioteka"
              subtitle="Szukaj po całej Wiedzy, gdy już wiesz czego potrzebujesz."
            />
          </div>
        </div>

        {recent.length > 0 ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Ostatnio czytane</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {recent.map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => openItem(item.id)}
                >
                  {item.starred ? <Star className="mr-1 h-3.5 w-3.5 fill-current text-amber-500" /> : null}
                  {item.title.length > 36 ? `${item.title.slice(0, 34)}…` : item.title}
                </Button>
              ))}
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardContent className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-end">
            <label className="min-w-[12rem] flex-1 space-y-1 text-sm">
              <span className="text-muted-foreground">Szukaj</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="np. hook, MOFU, Diament…"
                />
              </div>
            </label>
            <label className="space-y-1 text-sm sm:w-48">
              <span className="text-muted-foreground">Kategoria</span>
              <Select
                aria-label="Kategoria"
                value={category}
                onChange={setCategory}
                options={[
                  { value: "", label: "wszystkie" },
                  ...categories.map((c) => ({ value: c.id, label: c.label })),
                ]}
              />
            </label>
            <label className="space-y-1 text-sm sm:w-44">
              <span className="text-muted-foreground">Tag</span>
              <Select
                aria-label="Tag"
                value={tag}
                onChange={setTag}
                options={[
                  { value: "", label: "wszystkie" },
                  ...allTags.map((t) => ({ value: t, label: t })),
                ]}
              />
            </label>
            <Button
              type="button"
              variant={starsOnly ? "default" : "outline"}
              onClick={() => setStarsOnly((v) => !v)}
            >
              <Star className={cn("mr-1 h-4 w-4", starsOnly && "fill-current")} />
              Gwiazdki
            </Button>
            {(q || category || tag || starsOnly) && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setQ("");
                  setCategory("");
                  setTag("");
                  setStarsOnly(false);
                }}
              >
                <X className="mr-1 h-4 w-4" />
                Wyczyść
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Lista ({topLevel.length}
              {filtered.length !== items.length ? ` z ${items.length}` : ""})
            </CardTitle>
            {catalogQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Ładuję katalog…</p>
            ) : catalogQuery.isError ? (
              <p className="text-sm text-destructive">Nie udało się wczytać katalogu.</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Dostępne: {catalog?.available_count ?? 0}/{catalog?.count ?? 0}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {topLevel.map((item) => (
                <div key={item.id} className="space-y-1.5">
                  <ItemCard
                    item={item}
                    categoryLabel={categoryLabel(item.category)}
                    onOpen={() => openItem(item.id)}
                    onToggleStar={() => toggleStar(item)}
                  />
                  {childrenOf(item.id).map((child) => (
                    <div key={child.id} className="pl-2">
                      <ItemCard
                        item={child}
                        categoryLabel={categoryLabel(child.category)}
                        onOpen={() => openItem(child.id)}
                        onToggleStar={() => toggleStar(child)}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {!catalogQuery.isLoading && topLevel.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Nic nie pasuje do filtrów.</p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    );
  }

  const ctaTheme = phaseTheme(nextGuide?.phase.id || "orientacja");
  const CtaIcon = ctaIsResume ? BookOpen : ctaTheme.Icon;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Wiedza</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Najpierw ścieżka. Biblioteka jest na później.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={goLibrary}>
          <Library className="mr-1.5 h-4 w-4" />
          Biblioteka
        </Button>
      </div>

      {catalogQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Ładuję ścieżkę…</p>
      ) : catalogQuery.isError ? (
        <p className="text-sm text-destructive">Nie udało się wczytać katalogu.</p>
      ) : (
        <>
          {pathStats ? (
            <div className="rounded-xl border bg-card px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">Twój postęp na ścieżce</p>
                <p className="text-sm tabular-nums text-muted-foreground">
                  {pathStats.doneCount}/{pathStats.total} kroków · {pathStats.pct}%
                </p>
              </div>
              <ProgressBar pct={pathStats.pct} always className="mt-2" />
            </div>
          ) : null}

          <Card className={cn("border-2", ctaTheme.ring, ctaTheme.soft)}>
            <CardContent className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 gap-3">
                <span
                  className={cn(
                    "mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/80",
                    ctaTheme.accent,
                  )}
                >
                  <CtaIcon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className={cn("text-xs font-semibold uppercase tracking-wide", ctaTheme.accent)}>
                    {ctaIsResume ? "Wznów czytanie" : ctaItem ? "Następny krok" : "Ścieżka"}
                  </p>
                  <p className="mt-1 text-lg font-semibold leading-snug">
                    {ctaItem?.title || "Wszystkie kroki ukończone"}
                  </p>
                  {ctaItem && ctaIsResume ? (
                    <>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Przeczytane w {Math.round(ctaItem.scroll_pct || 0)}%. Wróć w to samo miejsce.
                      </p>
                      <ProgressBar
                        pct={ctaItem.scroll_pct || 0}
                        always
                        className="mt-2 max-w-xs"
                        barClassName={ctaTheme.bar}
                      />
                    </>
                  ) : nextGuide?.step.why ? (
                    <p className="mt-1 text-sm leading-snug text-muted-foreground">{nextGuide.step.why}</p>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">
                      Ścieżka domknięta. Możesz wracać do materiałów w Bibliotece.
                    </p>
                  )}
                </div>
              </div>
              {ctaItem ? (
                <Button type="button" size="lg" className="shrink-0" onClick={() => openItem(ctaItem.id)}>
                  <BookOpen className="mr-2 h-4 w-4" />
                  {ctaIsResume
                    ? `Wznów (${Math.round(ctaItem.scroll_pct || 0)}%)`
                    : "Otwórz"}
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={goLibrary}>
                  Przejdź do biblioteki
                </Button>
              )}
            </CardContent>
          </Card>

          {readingGuide?.phases?.length ? (
            <div>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Ścieżka
              </h2>
              <PathAccordion guide={readingGuide} itemsById={itemsById} items={items} onOpen={openItem} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Brak ścieżki w katalogu. Otwórz{" "}
              <button type="button" className="text-brand underline" onClick={goLibrary}>
                Bibliotekę
              </button>
              .
            </p>
          )}
        </>
      )}
    </div>
  );
}
